import { FooterActions, FooterButtons, PageFooter } from '@/Components/PageFooter/PageFooter';
import Title from '@/Components/Title/Title';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import CheckDetailStore from '@/Stores/CheckDetailStore';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { DobAlert, DobButton, DobDate, DobInput, DobLabel, DobRadioGroup, DobSelect, DobSwitch } from '@dedalusdiit/dob-components';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as API from '../../API';
import Styles from './CheckDetail.module.scss';

interface AddCheckProps {
    initialState: FilingCabinetItem;
}

const CheckDetail = observer(({ initialState }: AddCheckProps): React.ReactElement => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { removeActive } = useFilingCabinetContext();

    const fcContext = useFilingCabinetContext();
    const store = useMemo(() => new CheckDetailStore(id, new FilingCabinetStore(initialState, fcContext)), [initialState]);

    const typeOptions = [
        { value: 'DDL', label: t('Dropdown') },
        { value: 'FU', label: t('FieldUnit') },
        { value: 'FUD', label: t('FieldUnitDate') },
        { value: 'FUF', label: t('FieldUnitField') },
        { value: 'CDDL', label: t('CustomDropdown') },
    ];

    const genderOptions = [
        { value: null, label: t('Any') },
        { value: 'M', label: t('Male') },
        { value: 'F', label: t('Female') },
    ];

    const onAdd = (): void => {
        store.clearErrors();
        if (store.state.check.Name === '') {
            store.addError('name');
            return;
        }

        store.setAddCheckModelAges();
        void API.checks.add(store.state.check)
            .then((res) => {
                if (res.Error) {
                    store.setServerError(t('AnErrorOccured'));
                    return;
                }
                void removeActive();
            });
    };

    const onSave = async (): Promise<void> => {
        store.clearErrors();
        if (store.state.check.Name === '') {
            store.addError('name');
            return;
        }

        store.setAddCheckModelAges();
        await API.checks.save(store.state.check)
            .then((res) => {
                if (res.Error) {
                    store.setServerError(t('AnErrorOccured'));
                    return;
                }
                void removeActive();
            });
    };

    const handleTypeChange = (option: { label: string; value: string } | null): void => {
        if (!option) {
            return;
        }
        store.setCheckAttribute('TypeCode', option.value);
        store.setCheckAttribute('Unit', '');
        store.setCheckAttribute('RequiredToChangeToStatusStatus', 2);
        store.resetCustomDropdownValues();
        store.set('currentCustomInput', '');
    };

    const handleAgeChange = (alteredValue: string, value: string): void => {
        const newValue = Number.parseFloat(value);
        switch (alteredValue) {
            case 'MinAge':
                store.set('minAge', !Number.isNaN(newValue) ? newValue.toString() : '');
                break;
            case 'MaxAge':
                store.set('maxAge', !Number.isNaN(newValue) ? newValue.toString() : '');
                break;
        }
    };

    const validFrom = store.state.check.ValidFrom?.trim();
    const validTo = store.state.check.ValidTo?.trim();
    const fromDate = (validFrom != null && validFrom != '') ? new Date(validFrom) : null;
    const toDate = (validTo != null && validTo != '') ? new Date(validTo) : null;

    return (
        <div id={Styles.checkDetail}>
            { id == null
                ? <Title titleName={t('AddNewCheck')} />
                : <Title titleName={t('Edit') + ' ' + t('Check') + ' - ' + store.state.check.Name} />}

            <div className={Styles.content}>
                { store.state.serverError
                    && (
                        <div className={Styles.grid}>
                            <div className={Styles.row}>
                                <div />
                                <div style={{ marginBottom: '10px' }}>
                                    <DobAlert type="error">
                                        An error occurred
                                    </DobAlert>
                                </div>
                            </div>
                        </div>
                    )}
                <div className={Styles.grid}>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Name') + ':'} required />
                        </div>
                        <div>
                            <DobInput error={store.isError('name')} value={store.state.check.Name} onValueChange={(value) => store.setCheckAttribute('Name', value)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Remarks') + ':'} />
                        </div>
                        <div>
                            <DobInput value={store.state.check.Remarks} onValueChange={(value) => store.setCheckAttribute('Remarks', value)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Type') + ':'} />
                        </div>
                        <div>
                            <DobSelect
                                optionSelected={typeOptions.find((x) => x.value == store.state.check.TypeCode)}
                                onChange={handleTypeChange}
                                options={typeOptions}
                            />
                        </div>
                    </div>

                    <TypeDetails store={store} />

                    <div className={Styles.row}>
                        <div>
                            <DobLabel labelFor="CopyRemarksToOrder" text={t('CopyRemarksToOrder') + ':'} />
                        </div>
                        <div>
                            <DobSwitch id="CopyRemarksToOrder" checked={store.state.check.CopyRemarksToOrder} onChange={(checked) => store.setCheckAttribute('CopyRemarksToOrder', checked)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel labelFor="CopyRemarksToPatient" text={t('CopyRemarksToPatient') + ':'} />
                        </div>
                        <div>
                            <DobSwitch id="CopyRemarksToPatient" checked={store.state.check.CopyRemarksToPatient} onChange={(checked) => store.setCheckAttribute('CopyRemarksToPatient', checked)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel labelFor="GlobalCheck" text={t('GlobalCheck') + ':'} />
                        </div>
                        <div>
                            <DobSwitch id="GlobalCheck" checked={store.state.check.Global} onChange={(checked) => store.setCheckAttribute('Global', checked)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('ValidFrom') + ' ' + t('DDMMYYYY') + ':'} />
                        </div>
                        <div>
                            <DobDate selectedDate={fromDate} onChange={(value) => store.setValidFrom(value)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('ValidTo') + ' ' + t('DDMMYYYY') + ':'} />
                        </div>
                        <div>
                            <DobDate selectedDate={toDate} onChange={(value) => store.setValidTo(value)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Gender') + ':'} />
                        </div>
                        <div>
                            <DobSelect
                                optionSelected={genderOptions.find((x) => x.value == store.state.check.Gender)}
                                onChange={(option) => {
                                    if (option) {
                                        store.setCheckAttribute('Gender', option.value);
                                    }
                                }}
                                options={genderOptions}
                            />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('MinAge') + ':'} />
                        </div>
                        <div>
                            <DobInput value={store.state.minAge} onValueChange={(value) => handleAgeChange('MinAge', value)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('MaxAge') + ':'} />
                        </div>
                        <div>
                            <DobInput value={store.state.maxAge} onValueChange={(value) => handleAgeChange('MaxAge', value)} />
                        </div>
                    </div>

                    <div className={Styles.row}>
                        <div />
                        <div>
                            <DobRadioGroup
                                options={[{ value: 'EffAge', label: t('EffectiveAge') }, { value: 'YearAge', label: t('AgeBasedOnYear') }]}
                                value={store.state.typeAge}
                                onChange={(value) => store.set('typeAge', value)}
                            />
                        </div>
                    </div>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel labelFor="RequiredToChangeToStatus" text={t('RequiredToChangeToStatus') + ':'} />
                        </div>
                        <div>
                            <DobSwitch id="RequiredToChangeToStatus" checked={store.state.check.RequiredToChangeToStatus} onChange={(checked) => store.setCheckAttribute('RequiredToChangeToStatus', checked)} />
                        </div>
                    </div>

                    <StatusType store={store} />

                    <div className={Styles.row}>
                        <div>
                            <DobLabel labelFor="VisibleInTechnicianScreen" text={t('VisibleInTechnicianScreen') + ':'} />
                        </div>
                        <div>
                            <DobSwitch id="VisibleInTechnicianScreen" checked={store.state.check.VisibleInTechnicianScreen} onChange={(checked) => store.setCheckAttribute('VisibleInTechnicianScreen', checked)} />
                        </div>
                    </div>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel labelFor="RequiredToSaveAppointment" text={t('RequiredToSaveAppointment') + ':'} />
                        </div>
                        <div>
                            <DobSwitch id="RequiredToSaveAppointment" checked={store.state.check.RequiredToSaveAppointment} onChange={(checked) => store.setCheckAttribute('RequiredToSaveAppointment', checked)} />
                        </div>
                    </div>
                </div>
            </div>

            <PageFooter>
                <FooterActions>
                    { store.state.check.AddedOn !== '' ? <div>{t('AddedOn')}: {store.state.check.AddedOn}</div> : <div /> }
                    { store.state.check.LastModificationDateTime !== '' ? <div>{t('LastModification')}: {store.state.check.LastModificationDateTime} {t('By')}: {store.state.check.ModifiedBy}</div> : <div /> }
                </FooterActions>
                <FooterButtons>
                    <DobButton onClick={(_) => removeActive()} color="silent">{t('Close')}</DobButton>
                    { id == null ? <DobButton onClick={onAdd}>{t('Add')}</DobButton> : <></> }
                    { id != null ? <DobButton onClick={onSave}>{t('SaveAndClose')}</DobButton> : <></> }
                </FooterButtons>
            </PageFooter>
        </div>
    );
});

function TypeDetails({ store }: Readonly<{ store: CheckDetailStore }>): React.ReactElement {
    const { t } = useTranslation();

    const yesNoNvtOptions = [
        { value: '0', label: t('Unknown') },
        { value: '1', label: t('Yes') },
        { value: '2', label: t('No') },
        { value: '3', label: t('NVT') },
    ];

    const addCustomDropdownValue = () => {
        if (store.state.currentCustomInput === '') {
            return;
        }

        store.state.check.CustomDropdownValues.push(store.state.currentCustomInput);
        store.set('currentCustomInput', '');
    };

    switch (store.state.check.TypeCode) {
        case 'DDL':
            return (
                <div className={Styles.row}>
                    <div>
                        <DobLabel text={t('Preview') + ':'} />
                    </div>
                    <div>
                        <DobSelect
                            optionSelected={yesNoNvtOptions.find((x) => x.value == store.state.dropDownValue as unknown as string)}
                            onChange={(option) => {
                                if (option) {
                                    store.set('dropDownValue', option.value);
                                }
                            }}
                            options={yesNoNvtOptions}
                        />
                    </div>
                </div>
            );
        case 'FU':
            return (
                <>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Type') + ' ' + t('Detail') + ':'} />
                        </div>
                        <div>
                            <DobInput
                                value={store.state.check.Unit}
                                onValueChange={(value) => {
                                    store.setCheckAttribute('Unit', value);
                                }}
                                placeholder={t('unit')}
                            />
                        </div>
                    </div>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Preview') + ':'} />
                        </div>
                        <div className={Styles.flex}>
                            <DobInput value="60" disabled />
                            <DobInput value={store.state.check.Unit != '' ? store.state.check.Unit : 'ml'} disabled />
                        </div>
                    </div>
                </>
            );
        case 'FUD':
            return (
                <>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Type') + ':'} />
                        </div>
                        <div>
                            <DobInput
                                value={store.state.check.Unit}
                                onValueChange={(value) => {
                                    store.setCheckAttribute('Unit', value);
                                }}
                                placeholder={t('unit')}
                            />
                        </div>
                    </div>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Preview') + ':'} />
                        </div>
                        <div className={Styles.flex}>
                            <DobInput value="60" disabled />
                            <DobInput value={store.state.check.Unit != '' ? store.state.check.Unit : 'ml'} disabled />
                            <DobInput value="01/01/2025" disabled />
                        </div>
                    </div>
                </>
            );
        case 'FUF':
            return (
                <>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Type') + ' ' + t('Detail') + ':'} />
                        </div>
                        <div>
                            <DobInput
                                value={store.state.check.Unit}
                                onValueChange={(value) => store.setCheckAttribute('Unit', value)}
                                placeholder={t('unit')}
                            />
                        </div>
                    </div>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Preview') + ':'} />
                        </div>
                        <div className={Styles.flex}>
                            <DobInput value="60" disabled />
                            <DobInput value={store.state.check.Unit != '' ? store.state.check.Unit : 'ml'} disabled />
                            <DobInput value={t('Remark')} disabled />
                        </div>
                    </div>
                </>
            );
        case 'CDDL': {
            const currentValues = (): React.ReactElement => {
                return (
                    <div className={Styles.customValues}>
                        {store.state.check.CustomDropdownValues.map((value, index) => {
                            if (value === '---') {
                                return <></>;
                            }

                            return (
                                <div key={value}>
                                    <span>{value} </span>
                                    <DobButton color="silent" onClick={() => store.removeCustomDropdownOption(index)} icon="x" />
                                </div>
                            );
                        })}
                    </div>
                );
            };

            return (
                <>
                    <div className={Styles.row}>
                        <div>
                            <DobLabel text={t('Preview') + ':'} />
                        </div>
                        <div>
                            <DobSelect
                                optionSelected={{ value: '', label: '---' }}
                                onChange={() => {}}
                                isOptionDisabled={(option) => option != null}
                                options={[
                                    { value: '', label: '---' },
                                    ...store.state.check.CustomDropdownValues.filter((x) => x !== '---').map((x) => ({ value: x, label: x })),
                                ]}
                            />
                        </div>
                    </div>
                    <div className={Styles.row}>
                        <div />
                        <div>{currentValues()}</div>
                    </div>
                    <div className={Styles.row}>
                        <div />
                        <div className={Styles.flex}>
                            <DobInput
                                value={store.state.currentCustomInput}
                                onValueChange={(value) => store.set('currentCustomInput', value)}
                                placeholder={t('PressEnterToAddCustomValue')}
                            />
                            <DobButton onClick={addCustomDropdownValue}>{t('Add')}</DobButton>
                        </div>
                    </div>
                </>
            );
        }
        default:
            return <></>;
    }
}

const StatusType = ({ store }: Readonly<{ store: CheckDetailStore }>): React.ReactElement | null => {
    const { t } = useTranslation();

    if (!store.state.check.RequiredToChangeToStatus) {
        return null;
    }

    const statusOptions = [
        { value: 2, label: t('WaitingRoom') },
        { value: 3, label: t('Started') },
        { value: 4, label: t('Completed') },
        { value: 5, label: t('Dictated') },
    ];

    const selectedOption = store.state.check.RequiredToChangeToStatusStatus == 0 ? 2 : store.state.check.RequiredToChangeToStatusStatus; // Default is 2, but gets saved as 0 in DB if not necessary

    return (
        <div className={Styles.row}>
            <div>
                <DobLabel text={t('Status') + ':'} />
            </div>
            <div>
                <DobSelect
                    optionSelected={statusOptions.find((x) => x.value == selectedOption)}
                    onChange={(option) => {
                        if (option) {
                            store.setCheckAttribute('RequiredToChangeToStatusStatus', option.value);
                        }
                    }}
                    options={statusOptions}
                />
            </div>
        </div>
    );
};

export default CheckDetail;
